<template>
  <div class="pdf-viewer">
    <!-- PDF工具栏 -->
    <!-- <div class="pdf-toolbar" v-if="showToolbar">
      <div class="toolbar-left">
        <el-button size="small" @click="prevPage" :disabled="currentPage <= 1">
          <i class="el-icon-arrow-left"></i>
        </el-button>
        <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
        <el-button size="small" @click="nextPage" :disabled="currentPage >= totalPages">
          <i class="el-icon-arrow-right"></i>
        </el-button>
      </div>
      <div class="toolbar-right">
        <el-button size="small" @click="zoomOut">
          <i class="el-icon-zoom-out"></i>
        </el-button>
        <span class="scale-info">{{ Math.round(scale * 100) }}%</span>
        <el-button size="small" @click="zoomIn">
          <i class="el-icon-zoom-in"></i>
        </el-button>
        <el-button size="small" @click="fitWidth">适合宽度</el-button>
        <el-button size="small" @click="fitPage">适合页面</el-button>
        <el-button size="small" @click="toggleRotation" title="旋转PDF">
          <i class="el-icon-refresh"></i>
        </el-button>
      </div>
    </div> -->

    <!-- PDF内容区域 -->
    <div class="pdf-content" ref="pdfContent">
      <div v-if="loading" class="pdf-loading">
        <i class="el-icon-loading"></i>
        <p>PDF加载中...</p>
      </div>
      <div v-else-if="pdfUrl" class="pdf-pages" ref="pdfPages">
        <div
          v-for="n in totalPages"
          :key="n"
          class="pdf-page"
        >
          <canvas ref="pageCanvas" class="pdf-document"></canvas>
        </div>
      </div>
      <div v-else class="pdf-placeholder">
        <i class="el-icon-document"></i>
        <p>请选择PDF文件查看</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdfViewer',
  props: {
    // PDF文件URL
    pdfUrl: {
      type: String,
      default: ''
    },
    // 是否显示工具栏
    showToolbar: {
      type: Boolean,
      default: true
    },
    // 初始缩放比例
    initialScale: {
      type: Number,
      default: 1.0
    }
  },
  data() {
    return {
      loading: false,
      pdfDocument: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.0,
      pdfjsLib: null,
      rotation: 0,
      isRenderingAll: false,
      _scrollTargets: [],
      _io: null
    }
  },
  watch: {
    pdfUrl: {
      handler(newUrl) {
        if (newUrl) {
          this.loadPdfFile(newUrl);
        } else {
          this.resetPdf();
        }
      },
      immediate: true
    },
    initialScale: {
      handler(newScale) {
        this.scale = newScale;
        if (this.pdfDocument) {
          this.updatePdfDisplay();
        }
      }
    }
  },
  mounted() {
    if (!Array.isArray(this._scrollTargets)) {
      this._scrollTargets = []
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.addEventListener('scroll', this.handleScroll, { passive: true });
      this._scrollTargets.push(container);
    }
    // 绑定所有可滚动的祖先
    this.bindScrollableAncestors();
    // 监听窗口滚动与尺寸变化
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleScroll, { passive: true });
  },
  methods: {
    setupIntersectionObserver() {
      const canvases = this.$refs.pageCanvas;
      if (!canvases || (Array.isArray(canvases) && canvases.length === 0)) return;

      if (this._io) {
        try { this._io.disconnect(); } catch (e) {}
        this._io = null;
      }

      const container = this.$refs.pdfContent;
      const containerScrollable = container && (container.scrollHeight > container.clientHeight + 1);
      const root = containerScrollable ? container : null;

      const options = {
        root,
        threshold: [0, 0.1, 0.25, 0.33, 0.5, 0.66, 0.75, 0.9, 1]
      };

      const visibleRatios = new Map();
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const idx = (Array.isArray(canvases) ? canvases.indexOf(el) : 0);
          if (idx >= 0) {
            visibleRatios.set(idx + 1, entry.intersectionRatio || 0);
          }
        });
        let bestPage = this.currentPage;
        let bestRatio = -1;
        visibleRatios.forEach((ratio, page) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = page;
          }
        });
        if (bestPage !== this.currentPage && bestRatio >= 0) {
          this.currentPage = bestPage;
          this.$emit('page-changed', {
            currentPage: this.currentPage,
            totalPages: this.totalPages
          });
        }
      }, options);

      (Array.isArray(canvases) ? canvases : [canvases]).forEach(cv => {
        if (cv) this._io.observe(cv);
      });
    },
    bindScrollableAncestors() {
      let el = this.$el && this.$el.parentElement;
      const seen = new Set();
      while (el && el !== document.body && el !== document.documentElement) {
        if (seen.has(el)) break;
        seen.add(el);
        try {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY;
          const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1;
          if (isScrollable) {
            el.addEventListener('scroll', this.handleScroll, { passive: true });
            this._scrollTargets.push(el);
          }
        } catch (e) {}
        el = el.parentElement;
      }
    },
    // 加载PDF.js库
    async loadPdfJs() {
      if (this.pdfjsLib) {
        return this.pdfjsLib;
      }

      try {
        // 动态加载PDF.js
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
        script.async = true;

        return new Promise((resolve, reject) => {
          script.onload = () => {
            this.pdfjsLib = window.pdfjsLib;
            // 设置worker路径
            this.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
            resolve(this.pdfjsLib);
          };
          script.onerror = () => {
            reject(new Error('PDF.js加载失败'));
          };
          document.head.appendChild(script);
        });
      } catch (error) {
        console.error('PDF.js加载失败：', error);
        throw error;
      }
    },

    // 加载PDF文件
    async loadPdfFile(src) {
      try {
        this.loading = true;
        this.currentPage = 1;
        this.scale = this.initialScale;

        // 确保PDF.js已加载
        const pdfjsLib = await this.loadPdfJs();
        if (!pdfjsLib) {
          throw new Error('PDF.js加载失败');
        }
        let source = null;
        const s = (src || '').trim();
        if (!s) throw new Error('无有效的PDF');
        // 判断数据是否是base64
        const isDataUrl = s.startsWith('data:application/pdf;base64,');
        // 判断数据是否是base64
        const isLikelyBase64 = !/^https?:\/\//i.test(s) && !/^\//.test(s) && !/^blob:/i.test(s) && /^[A-Za-z0-9+/=\s]+$/.test(s);
        // 是base64
        if (isDataUrl) {
          const base64 = s.split(',')[1] || '';
          source = { data: this.base64ToUint8Array(base64) };
          // 不是base64
        } else if (isLikelyBase64) {
          source = { data: this.base64ToUint8Array(s) };
        } else {
          // 不是base64
          source = { url: s };
        }
        // 加载PDF
        const loadingTask = pdfjsLib.getDocument(Object.assign({
          verbosity: 0,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/cmaps/',
          cMapPacked: true
        }, source));
        // 等待PDF加载完成
        this.pdfDocument = await loadingTask.promise;
        // 获取总页数
        this.totalPages = this.pdfDocument.numPages;
        // PDF加载完成
        this.loading = false;
        // 等待DOM更新后再渲染第一页
        await this.$nextTick();
        // 更新PDF显示
        await this.updatePdfDisplay();
        // 初始化当前页
        this.$nextTick(() => this.handleScroll());

        console.log('PDF加载完成，总页数：', this.totalPages);
        this.$emit('pdf-loaded', {
          totalPages: this.totalPages,
          currentPage: this.currentPage
        });
      } catch (error) {
        console.error('PDF加载失败：', error);

        // 检查是否是CORS错误
        if (error.message && error.message.includes('Failed to fetch')) {
          this.$message.error('PDF加载失败: 跨域访问被阻止，请确保PDF文件支持CORS或使用同域文件');
        } else {
          // this.$message.error('PDF文件加载失败: ' + error.message);
        }

        this.loading = false;
        this.$emit('pdf-error', error);
      }
    },

    base64ToUint8Array(base64) {
      const cleaned = base64.replace(/\s+/g, '');
      const binaryString = atob(cleaned);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    },

    // 渲染指定页面
    async renderPage(pageNumber) {
      if (!this.pdfDocument) {
        console.warn('PDF文档未加载');
        return;
      }

      try {
        const page = await this.pdfDocument.getPage(pageNumber);
        const canvases = this.$refs.pageCanvas;
        const canvas = Array.isArray(canvases) ? canvases[pageNumber - 1] : canvases;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        // 添加旋转参数来修正PDF显示方向
        const viewport = page.getViewport({ 
          scale: this.scale,
          rotation: this.rotation  // 使用动态旋转角度
        });

        // 设置canvas尺寸
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // 渲染页面
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        await page.render(renderContext).promise;

        this.$emit('page-rendered', {
          pageNumber: pageNumber,
          totalPages: this.totalPages
        });
      } catch (error) {
        
        // 特殊处理渲染取消错误
        if (error.name === 'RenderingCancelledException') {
          console.log('PDF渲染被取消，这通常是由于页面切换或组件销毁导致的');
          return;
        }
        this.$emit('render-error', error);
      }
    },

    // 更新PDF显示
    async updatePdfDisplay() {
      if (!this.pdfDocument || this.isRenderingAll) return;
      this.isRenderingAll = true;
      try {
        for (let i = 1; i <= this.totalPages; i++) {
          await this.renderPage(i);
        }
        await this.$nextTick();
        this.setupIntersectionObserver();
      } finally {
        this.isRenderingAll = false;
      }
    },

    // 上一页
    async prevPage() {
      if (this.currentPage > 1) {
        this.scrollToPage(this.currentPage - 1);
      }
    },

    // 下一页
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.scrollToPage(this.currentPage + 1);
      }
    },

    // 放大
    async zoomIn() {
      this.scale = Math.min(this.scale + 0.2, 3.0);
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    // 缩小
    async zoomOut() {
      this.scale = Math.max(this.scale - 0.2, 0.5);
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    // 适合宽度
    async fitWidth() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    // 适合页面
    async fitPage() {
      this.scale = 1.0;
      await this.updatePdfDisplay();
      this.$emit('scale-changed', this.scale);
    },

    // 跳转到指定页面
    async goToPage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.scrollToPage(pageNumber);
      }
    },

    // 重置PDF
    resetPdf() {
      this.loading = false;
      this.pdfDocument = null;
      this.currentPage = 1;
      this.totalPages = 0;
      this.scale = this.initialScale;
    },

    // 切换PDF旋转角度
    toggleRotation() {
      this.rotation = this.rotation === 0 ? 180 : 0;
      console.log('切换PDF旋转角度:', this.rotation);
      
      // 重新渲染当前页面
      if (this.pdfDocument) {
        this.updatePdfDisplay();
      }
    },

    // 获取当前PDF信息
    getPdfInfo() {
      return {
        currentPage: this.currentPage,
        totalPages: this.totalPages,
        scale: this.scale,
        loading: this.loading,
        hasPdf: !!this.pdfDocument
      };
    },

    scrollToPage(pageNumber) {
      const container = this.$refs.pdfContent;
      const canvases = this.$refs.pageCanvas;
      const target = Array.isArray(canvases) ? canvases[pageNumber - 1] : canvases;
      if (!container || !target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    handleScroll(evt) {
      const container = this.$refs.pdfContent;
      const canvases = this.$refs.pageCanvas || [];
      if (!canvases || canvases.length === 0) return;

      let baseRect = null;
      // 优先使用触发滚动的目标容器
      if (evt && evt.target && evt.target.getBoundingClientRect && evt.target.contains && evt.target.contains(this.$el)) {
        baseRect = evt.target.getBoundingClientRect();
      }
      // 其次使用已绑定的可滚动祖先中，离组件最近的一个
      if (!baseRect && Array.isArray(this._scrollTargets) && this._scrollTargets.length > 0) {
        for (let i = 0; i < this._scrollTargets.length; i++) {
          const t = this._scrollTargets[i];
          if (t && t.getBoundingClientRect && t.contains && t.contains(this.$el)) {
            baseRect = t.getBoundingClientRect();
            break;
          }
        }
      }
      // 再次回退到组件自身容器
      if (!baseRect && container && container.getBoundingClientRect) {
        baseRect = container.getBoundingClientRect();
      }
      // 最后回退到窗口
      const containerMid = baseRect ? (baseRect.top + baseRect.height / 2) : (window.innerHeight / 2);
      let closestPage = 1;
      let closestDist = Infinity;
      (Array.isArray(canvases) ? canvases : [canvases]).forEach((cv, idx) => {
        const rect = cv.getBoundingClientRect();
        const mid = rect.top + (rect.height || 1) / 2;
        const dist = Math.abs(mid - containerMid);
        if (dist < closestDist) {
          closestDist = dist;
          closestPage = idx + 1;
        }
      });

      if (closestPage !== this.currentPage) {
        this.currentPage = closestPage;
        this.$emit('page-changed', {
          currentPage: this.currentPage,
          totalPages: this.totalPages
        });
      }
    }
  },
  updated() {
    if (this.pdfDocument) {
      this.setupIntersectionObserver();
    }
  },
  beforeDestroy() {
    // 清理资源
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
    }
    const container = this.$refs.pdfContent;
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
    }
    // 移除所有绑定的祖先滚动监听
    if (Array.isArray(this._scrollTargets)) {
      this._scrollTargets.forEach(t => {
        try { t.removeEventListener('scroll', this.handleScroll); } catch (e) {}
      });
      this._scrollTargets = [];
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
    if (this._io) {
      try { this._io.disconnect(); } catch (e) {}
      this._io = null;
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-info,
.scale-info {
  font-size: 14px;
  color: #666;
  min-width: 60px;
  text-align: center;
}

.pdf-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  background: #f9f9f9;
}

.pdf-pages {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
}

.pdf-page {
  display: flex;
  justify-content: center;
}

.pdf-loading {
  text-align: center;
  color: #666;
}

.pdf-loading i {
  font-size: 24px;
  margin-bottom: 10px;
}

.pdf-placeholder {
  text-align: center;
  color: #999;
}

.pdf-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.pdf-document {
  max-width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background: white;
}
</style>

